import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRoleStore } from "../../store";
import { useTranslation } from "react-i18next";
import { RoleInputI } from "interfaces/Role";
import { AddRoleService, PatchRoleService } from "services";
import { getCompanys } from "services/service.cruds/company/company";
import {
  getDefaultValueForSelect,
  validateYupStringWithSpecialCharacters,
} from "helpers/functionsHelpers";
import { OptionSelect } from "Common/Components";
import { NotificationType, showNotifications } from "helpers/toas_notification";

export const useAddRoles = () => {
  const navigate = useNavigate();
  const [isSubmitRoles, setIsSubmitRoles] = useState(false);
  const [valueCompany, setValueCompany] = useState<OptionSelect>();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [optionCompany, setOptionCompany] = useState<OptionSelect[]>([
    {
      label: t('selectComponent.defaultValue'),
      value: '0',
      isDisabled: true,
    },
  ]);

  const {
    handleCloseModalRole,
    role,
    isEdit,
    setIsEdit,
    setRole 
  } = useRoleStore();

  const initialValues = {
    description: role?.description ?? '',
    company_id: role?.company.id ?? '',
  };

  const validationSchema = Yup.object().shape({
    description: validateYupStringWithSpecialCharacters(t, true),
    company_id: Yup.number().required(t('validationForms.required')),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      const errors = await formik.validateForm();

      if (Object.keys(errors).length > 0) {
        setErrors(errors);
        setSubmitting(false);

        return;
      }
      setSubmitting(true);

      const rolesData: RoleInputI = {
        id: isEdit ? role?.id : undefined,
        description: values.description,
        company_id: values.company_id,
      };

      try {
        if (isEdit && role) {
          await handleEditRoles(rolesData, role.id)
        } else {
          await handleCreateRoles(rolesData)
        }
      } catch (error) {
        showNotifications(NotificationType.ERROR, t("notificationsMessages.error") + error);
      } finally {
        setSubmitting(false);
      }
    }
  })

  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: async () =>  {
      const response = await getCompanys();
      return response.data;
    }
  });

  useEffect(() => {
    if (companies) {
      const options: OptionSelect[] = [];

      companies.map((company) => {
        options.push({
          label: company.name,
          value: company.id.toString(),
        });
      });

      setOptionCompany(options);
    }
  }, [companies]);

  useEffect(() => {
    const defaultValueFind = getDefaultValueForSelect(
      optionCompany,
      formik.values.company_id.toString()
    );
    setValueCompany(defaultValueFind);
  }, [formik.values.company_id, optionCompany]);

  const handleCreateRoles = async (rolesData: RoleInputI) => {
    try {
      const response = await AddRoleService(rolesData);

      if (response.code === '200') {
        showNotifications(
          NotificationType.SUCCESS,
          t('notificationsMessages.successAdd')
        )
        queryClient.invalidateQueries({
          queryKey: ['security-service-roles']
        });
        setRole(null);
        handleCloseModalRole();
        formik.resetForm();
      } else {
        setRole(null);
        showNotifications(
          NotificationType.ERROR,
          t('notificationsMessages.error') + response.message
        );
      }
    } catch (error) {
      setRole(null);
      showNotifications(
        NotificationType.ERROR,
        t('notificationsMessages.error') + error
      );
    }
  };

  const handleEditRoles = async (rolesData: RoleInputI, id: number) => {
    try {
      if (!role) return;

      const response = await PatchRoleService(id, rolesData);

      if (response.code === '200') {
        showNotifications(
          NotificationType.SUCCESS,
          t('notificationsMessages.success')
        );
        setIsEdit(false);
        setRole(null);
        queryClient.invalidateQueries({
          queryKey: ['security-service-roles'],
        });
        handleCloseModalRole();
      } else {
        setIsEdit(false);
        setRole(null);
        showNotifications(
          NotificationType.ERROR,
          t('notificationsMessages.error') + response.message
        );
      }

    } catch (error) {
      setIsEdit(false);
      setRole(null);
      showNotifications(NotificationType.ERROR,
        t('notificationsMessages.error') + error
      );
    }
  };

  return {
    navigate,
    isSubmitRoles,
    formik,
    optionCompany,
    valueCompany,
  };
};
